import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

import type { IPlatformAdmin } from "../../interfaces";

export default function usePlatformAdmin() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();
    const queryClient = useQueryClient();

    function useList() {
        return useQuery({
            queryKey: ["platform-admins"],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/user/platform-admins");
                return parseSuccessResponseData(response) as IPlatformAdmin[];
            },
        });
    }

    function useDescribe(id?: string) {
        return useQuery({
            queryKey: ["platform-admins", id],
            queryFn: async () => {
                const response = await axiosPrivate.get(`/v1/user/platform-admins/${id}`);
                return parseSuccessResponseData(response) as IPlatformAdmin;
            },
            enabled: !!id,
        });
    }

    function useCreate() {
        return useMutation({
            mutationFn: (parameters: {
                FirstName: string;
                LastName: string;
                Email: string;
                Password?: string;
            }) =>
                axiosPrivate
                    .post("/v1/user/platform-admins", parameters)
                    .then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["platform-admins"] });
            },
        });
    }

    function useUpdate() {
        return useMutation({
            mutationFn: (parameters: {
                Id: string;
                FirstName: string;
                LastName: string;
                IsActive: boolean;
            }) => {
                const { Id, ...body } = parameters;
                return axiosPrivate
                    .put(`/v1/user/platform-admins/${Id}`, body)
                    .then(parseSuccessResponseData) as Promise<IPlatformAdmin>;
            },
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({ queryKey: ["platform-admins"] });
                queryClient.invalidateQueries({ queryKey: ["platform-admins", variables.Id] });
            },
        });
    }

    function useRevoke() {
        return useMutation({
            mutationFn: (parameters: { Id: string }) =>
                axiosPrivate
                    .delete(`/v1/user/platform-admins/${parameters.Id}`)
                    .then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["platform-admins"] });
            },
        });
    }

    return {
        useList,
        useDescribe,
        useCreate,
        useUpdate,
        useRevoke,
    };
}
